import { createContext, useContext, useEffect, useRef } from 'react';
import { useSessionStorage } from 'Utilities/UseStorage';
import { useUserData } from './UserContext';
import { GetUserCampaigns } from './UserApi';
import { IUserData } from './UserContextModels';

export type IUserCampaign = Record<string, any>;

export type IUserCampaignsContext = {
  userCampaigns: IUserCampaign[] | null;
  getUserCampaigns: (
    resetCache?: boolean,
    userStatus?: string | null,
    initiativeStatus?: string | null,
    initiativeType?: string | null,
  ) => void;
};

const initialState: IUserCampaignsContext = {
  userCampaigns: null,
  getUserCampaigns: () => {},
};

export const UserCampaignsContext = createContext(initialState);
export const placeholderKey = 'UserCampaigns_PLACEHOLDER';

export function craftStorageKey(userData: IUserData | undefined) {
  if (userData?.dpecid && userData?.channel) {
    return `${userData.isMockingAgent ? 'mocking_' : ''}UserCampaigns_${userData.dpecid}_${userData.channel}`;
  }
  return placeholderKey;
}

export function UserCampaignsContextProvider({ children }: { children: React.ReactNode }) {
  const { userData } = useUserData();
  const expirationMinutes = 240;
  const sessionStorageKey = craftStorageKey(userData);
  const lastFetchAbortRef = useRef<AbortController | null>(null);
  const [userCampaigns, setUserCampaigns] = useSessionStorage<IUserCampaign[]>(
    sessionStorageKey,
    undefined,
    expirationMinutes,
  );

  const areCampaignsEqual = (
    a: IUserCampaign[] | null | undefined,
    b: IUserCampaign[] | null | undefined,
  ): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  };

  const getUserCampaigns = async (
    resetCache: boolean = false,
    userStatus: string | null = null,
    initiativeStatus: string | null = null,
    initiativeType: string | null = null,
  ) => {
    if (!userData?.dpecid || (!userData?.isPL() && !userData?.isBL())) {
      return;
    }

    if (!resetCache && userCampaigns !== undefined) {
      return;
    }

    if (lastFetchAbortRef.current) {
      lastFetchAbortRef.current.abort();
    }
    const abortController = new AbortController();
    lastFetchAbortRef.current = abortController;

    try {
      const next = await GetUserCampaigns(
        resetCache,
        userData.dpecid,
        userData.isPL() ? 'PL' : 'BL',
        userStatus,
        initiativeStatus,
        initiativeType,
      );

      if (abortController.signal.aborted) return;

      if (!areCampaignsEqual(userCampaigns, next)) {
        setUserCampaigns({ data: next });
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      console.error(
        `ERROR in UserCampaignsContext.getUserCampaigns(): userId=${userData?.dpecid} channel=${userData?.channel} ${err}`,
      );
      if (!areCampaignsEqual(userCampaigns, [])) {
        setUserCampaigns({ data: [] });
      }
    }
  };

  useEffect(() => {
    if (sessionStorageKey === placeholderKey || !sessionStorageKey.includes('mocking_')) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.includes('mocking_UserCampaigns')) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    }
  }, [sessionStorageKey]);

  const shared: IUserCampaignsContext = {
    userCampaigns: userCampaigns ?? null,
    getUserCampaigns,
  };

  return <UserCampaignsContext.Provider value={shared}>{children}</UserCampaignsContext.Provider>;
}

export function useUserCampaigns() {
  const context = useContext(UserCampaignsContext);
  if (context === undefined) {
    throw new Error('useUserCampaigns must be used within a UserCampaignsContextProvider');
  }
  return context;
}
